import React, { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import {
  MdHome,
  MdTrendingUp,
  MdAccountBalanceWallet,
  MdSpeed,
  MdHistory,
  MdOutlineTimer,
  MdOutlineTrendingFlat,
} from "react-icons/md";
import Card from "../../../components/card";
import SkeletonTable from "../../../components/skeleton/table";
import KpiCard from "../../../components/kpi/KpiCard";
import { DashboardHome } from "../../../interfaces/Dashboard";
import { getHome } from "../../../services/dashboardApi";
import AlertStrip from "./components/AlertStrip";
import ReceivablesFunnelCard from "./components/ReceivablesFunnelCard";
import UnbilledHoursCard from "./components/UnbilledHoursCard";
import UtilizationForecastCard from "./components/UtilizationForecastCard";
import ProjectsAtRiskList from "./components/ProjectsAtRiskList";
import MyWeekCard from "./components/MyWeekCard";
import ViewToggle, { HomeView } from "./components/ViewToggle";
import ClientHoursTable from "./components/ClientHoursTable";
import TeamLoadList from "./components/TeamLoadList";
import MonthPicker, { MonthValue, currentMonth } from "./components/MonthPicker";
import { HistoryMode } from "./components/HistoryChart";

// Único ponto do portal que carrega ApexCharts (~500 KB, sem tree-shaking). Em chunk separado
// para não pesar no carregamento inicial — o CRA não faz code splitting sozinho.
const HistoryChart = React.lazy(() => import("./components/HistoryChart"));

/**
 * Tela inicial do portal, com duas lentes sobre a mesma operação:
 *
 *   **Semana** — gestão do time: quem está estourado, quem está ocioso, o que vem pela frente.
 *   **Mês**    — horas e dinheiro: MTD contra capacity, projeção de fechamento e comparativos.
 *
 * O toggle é o mecanismo anti-entulho: em vez de empilhar tudo numa página só, cada cadência
 * carrega apenas o que ela decide. Os alertas ficam fora dele — risco não tem cadência.
 *
 * Dentro de cada lente a ordem é a ordem em que se decide: (1) o que está em risco, (2) o dinheiro,
 * (3) a capacidade, (4) o meu trabalho.
 *
 * ⚠ Bloco `null` = papel sem permissão ⇒ não renderiza. Lista vazia = sem dado ⇒ estado vazio.
 */

const MONTHS = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

const fmtMoney = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const fmtMoneyK = (v: number) =>
  v >= 1000 ? `R$ ${(v / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mil` : fmtMoney(v);
const fmtHours = (h: number) => `${h.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} h`;
const fmtHours0 = (h: number) => `${h.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} h`;

const HomePage: React.FC = () => {
  const [view, setView] = useState<HomeView>("week");
  const [histMode, setHistMode] = useState<HistoryMode>("revenue");
  // Abre sempre no mês corrente. Estado local, não persistido: voltar à home amanhã tem de mostrar
  // hoje, não o mês que alguém abriu semana passada para conferir uma coisa.
  const [period, setPeriod] = useState<MonthValue>(currentMonth);
  const [data, setData] = useState<DashboardHome | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Uma chamada só, por promessa da change: tudo o que a tela desenha — nas duas lentes — vem do
  // agregador. Números de capacity continuam calculados pelo módulo de Capacity, apenas viajam
  // junto em vez de exigirem um segundo request.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getHome(period.month, period.year));
    } catch (e: any) {
      console.error("Erro ao carregar a home:", e);
      setError(
        e?.response?.status === 403
          ? "Seu perfil não tem acesso a esta tela."
          : "Não foi possível carregar os indicadores."
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  // O período que o servidor REALMENTE apurou — pode diferir do filtro enquanto a carga não volta.
  const served = data?.period;
  const monthLabel = served ? `${MONTHS[served.month - 1]} de ${served.year}` : "";
  const previousLabel = served ? MONTHS[(served.month + 10) % 12].slice(0, 3) : "mês anterior";

  const weekRangeLabel = useMemo(() => {
    if (!served) return "";
    const start = new Date(served.weekStart);
    const end = new Date(start);
    end.setDate(end.getDate() + 4);
    const d = (x: Date) => x.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
    return `${d(start)} – ${d(end)}`;
  }, [served]);

  const showMonthBlocks = view === "month";
  const showWeekBlocks = view === "week";

  return (
    <div className="mt-5 flex animate-fade-up flex-col gap-5">
      {/* Cabeçalho + lente */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex items-center gap-4">
          <span
            className="dv-aura flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-dv-gradient text-white shadow-dv-glow"
            aria-hidden="true"
          >
            <MdHome className="h-6 w-6" />
          </span>
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-dv-blue-500/10 px-3 py-1 font-display text-xs font-bold uppercase tracking-[0.18em] text-dv-blue-600 dark:text-dv-cyan-300">
              Cockpit
            </span>
            <h1 className="mt-1.5 text-2xl font-extrabold tracking-tight text-navy-700 dark:text-white sm:text-3xl">
              {view === "week"
                ? weekRangeLabel || "A semana"
                : monthLabel
                ? monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)
                : "O mês"}
            </h1>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <ViewToggle view={view} onChange={setView} />
          {view === "month" ? (
            <MonthPicker
              value={period}
              onChange={setPeriod}
              disabled={loading}
              progressLabel={
                data
                  ? `dia útil ${data.period.businessDaysElapsed} de ${data.period.businessDaysTotal}`
                  : undefined
              }
            />
          ) : (
            weekRangeLabel && (
              <span className="rounded-full border border-gray-200 bg-white px-4 py-2 font-mono text-[11.5px] text-gray-600 dark:border-white/10 dark:bg-navy-800 dark:text-gray-300">
                {weekRangeLabel}
              </span>
            )
          )}
        </div>
      </div>

      {loading ? (
        <SkeletonTable active={true} />
      ) : error ? (
        <Card extra="p-8 text-center">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{error}</p>
        </Card>
      ) : data ? (
        <>
          {/* 1 · Risco — fora do toggle: risco não tem cadência */}
          <AlertStrip
            alerts={
              showMonthBlocks
                ? data.alerts.filter((a) => a.code !== "over_allocated" && a.code !== "no_timesheet_this_week")
                : data.alerts
            }
          />

          {/* ══ LENTE SEMANA ══ */}
          {showWeekBlocks && data.utilization && (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <KpiCard
                icon={<MdSpeed className="h-6 w-6" aria-hidden="true" />}
                label="Utilização planejada"
                value={`${Math.round(data.utilization.plannedUtilizationPct)}%`}
                accent="blue"
                progress={{ value: data.utilization.plannedUtilizationPct, target: data.utilization.target }}
                hint={`${fmtHours(data.utilization.teamPlannedHours)} planejadas de ${fmtHours(
                  data.utilization.teamCapacityHours
                )}`}
                href="/admin/capacity"
              />
              <KpiCard
                icon={<MdHistory className="h-6 w-6" aria-hidden="true" />}
                label="Semana passada, realizada"
                value={`${Math.round(data.utilization.previousWeekWorkedUtilizationPct)}%`}
                accent="cyan"
                hint={`${fmtHours(data.utilization.previousWeekWorkedHours)} apontadas de ${fmtHours(
                  data.utilization.previousWeekCapacityHours
                )} — a diferença mora em quem não apontou`}
                href="/admin/hours-management"
              />
              <KpiCard
                icon={<MdOutlineTimer className="h-6 w-6" aria-hidden="true" />}
                label="Sobre-alocação"
                value={
                  data.utilization.overAllocatedCount === 1
                    ? "1 pessoa"
                    : `${data.utilization.overAllocatedCount} pessoas`
                }
                accent={data.utilization.overAllocatedCount > 0 ? "red" : "emerald"}
                hint={
                  data.utilization.withoutTimesheetCount > 0
                    ? `${data.utilization.withoutTimesheetCount} sem apontamento na semana passada`
                    : "Todo mundo apontou na semana passada"
                }
                href="/admin/capacity"
              />
              <KpiCard
                icon={<MdOutlineTrendingFlat className="h-6 w-6" aria-hidden="true" />}
                label="Capacidade vendável"
                value={fmtHours0(data.utilization.sellableHoursAhead)}
                accent="amber"
                hint={`livre nas próximas ${data.utilization.sellableWeeksAhead} semanas — o gráfico abaixo mostra onde`}
                href="/admin/capacity"
              />
            </div>
          )}

          {showWeekBlocks && (data.utilizationSeries || data.teamWeekLoad) && (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
              {data.utilizationSeries && (
                <div className="lg:col-span-5">
                  <UtilizationForecastCard points={data.utilizationSeries} />
                </div>
              )}
              {data.teamWeekLoad && (
                <div className="lg:col-span-7">
                  <TeamLoadList view="week" weekRows={data.teamWeekLoad} />
                </div>
              )}
            </div>
          )}

          {showWeekBlocks && data.projectsAtRisk && <ProjectsAtRiskList projects={data.projectsAtRisk} />}

          {/* ══ LENTE MÊS ══ */}
          {showMonthBlocks && (data.revenue || data.monthHours || data.receivables) && (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {data.revenue && (
                <KpiCard
                  icon={<MdTrendingUp className="h-6 w-6" aria-hidden="true" />}
                  label="Faturamento realizado"
                  value={fmtMoneyK(data.revenue.realized)}
                  accent="emerald"
                  progress={{ value: data.revenue.realized, target: data.revenue.target }}
                  hint={`Projeção ${fmtMoneyK(data.revenue.projection)} · até ${previousLabel}: ${fmtMoneyK(
                    data.revenue.previousMonthRealizedSamePeriod
                  )}${
                    data.revenue.realizedDeltaPct != null
                      ? ` (${data.revenue.realizedDeltaPct >= 0 ? "+" : ""}${data.revenue.realizedDeltaPct.toLocaleString(
                          "pt-BR",
                          { maximumFractionDigits: 1 }
                        )}%)`
                      : ""
                  }`}
                  href="/admin/financial"
                />
              )}
              {data.monthHours && (
                <KpiCard
                  icon={<MdOutlineTimer className="h-6 w-6" aria-hidden="true" />}
                  label="Horas vs capacity"
                  value={`${fmtHours0(data.monthHours.workedMtd)} · ${Math.round(
                    data.monthHours.utilizationMtdPct
                  )}%`}
                  accent="blue"
                  progress={{ value: data.monthHours.utilizationMtdPct, target: 100, label: "do capacity" }}
                  hint={`de ${fmtHours0(data.monthHours.capacityMtd)} até hoje · ${previousLabel}: ${fmtHours0(
                    data.monthHours.previousMonthWorkedSamePeriod
                  )}${
                    data.monthHours.workedDeltaPct != null
                      ? ` (${data.monthHours.workedDeltaPct >= 0 ? "+" : ""}${data.monthHours.workedDeltaPct.toLocaleString(
                          "pt-BR",
                          { maximumFractionDigits: 1 }
                        )}%)`
                      : ""
                  }`}
                  href="/admin/hours-management"
                />
              )}
              {data.monthHours && (
                <KpiCard
                  icon={<MdSpeed className="h-6 w-6" aria-hidden="true" />}
                  label="Projeção de horas do mês"
                  value={fmtHours0(data.monthHours.projectedMonthHours)}
                  accent="cyan"
                  hint={`apontado + ${fmtHours0(
                    data.monthHours.remainingPlannedHours
                  )} planejadas · ${previousLabel} fechou em ${fmtHours0(data.monthHours.previousMonthTotalHours)}${
                    data.monthHours.projectedDeltaPct != null
                      ? ` (${data.monthHours.projectedDeltaPct >= 0 ? "+" : ""}${data.monthHours.projectedDeltaPct.toLocaleString(
                          "pt-BR",
                          { maximumFractionDigits: 1 }
                        )}%)`
                      : ""
                  }`}
                  href="/admin/capacity"
                />
              )}
              {data.receivables && (
                <KpiCard
                  icon={<MdAccountBalanceWallet className="h-6 w-6" aria-hidden="true" />}
                  label="A receber"
                  value={fmtMoneyK(data.receivables.totalOpen)}
                  accent={data.receivables.agingOverThresholdCount > 0 ? "amber" : "cyan"}
                  hint={
                    data.receivables.agingOverThresholdCount > 0
                      ? `${fmtMoneyK(data.receivables.agingOverThresholdAmount)} há +${
                          data.receivables.agingThresholdDays
                        } dias · recebido no mês: ${fmtMoneyK(data.receivables.receivedThisMonth)}`
                      : `recebido no mês: ${fmtMoneyK(data.receivables.receivedThisMonth)}`
                  }
                  href="/admin/financial"
                />
              )}
            </div>
          )}

          {showMonthBlocks && (data.revenueSeries || data.receivables) && (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
              {data.revenueSeries && data.revenueSeries.length > 0 && (
                <Card extra="p-5 lg:col-span-8">
                  <div className="mb-2 flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-base font-bold text-navy-700 dark:text-white">Últimos 12 meses</h3>
                      <p className="text-xs text-gray-700 dark:text-gray-300">
                        {histMode === "revenue"
                          ? "Faturado por competência, recebido por caixa, meta com vigência."
                          : "Horas apontadas contra o capacity efetivo (feriados já descontados)."}
                      </p>
                    </div>
                    <div
                      role="group"
                      aria-label="Série do histórico"
                      className="inline-flex gap-0.5 rounded-lg border border-gray-200 bg-gray-50 p-0.5 dark:border-white/10 dark:bg-navy-900"
                    >
                      {([
                        { k: "revenue" as const, l: "Faturamento" },
                        { k: "hours" as const, l: "Horas" },
                      ]).map((t) => (
                        <button
                          key={t.k}
                          type="button"
                          aria-pressed={histMode === t.k}
                          onClick={() => setHistMode(t.k)}
                          className={`rounded-md px-3 py-1.5 font-display text-[11px] font-bold transition-colors ${
                            histMode === t.k
                              ? "bg-white text-navy-700 shadow-sm dark:bg-navy-700 dark:text-white"
                              : "text-gray-500 hover:text-navy-700 dark:text-gray-400 dark:hover:text-white"
                          }`}
                        >
                          {t.l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Suspense
                    fallback={
                      <div className="flex h-[300px] items-center justify-center text-sm text-gray-700 dark:text-gray-300">
                        Carregando gráfico…
                      </div>
                    }
                  >
                    <HistoryChart points={data.revenueSeries} mode={histMode} />
                  </Suspense>
                </Card>
              )}
              {data.receivables && (
                <div className="lg:col-span-4">
                  <ReceivablesFunnelCard data={data.receivables} />
                </div>
              )}
            </div>
          )}

          {showMonthBlocks && (data.clientHours || data.employeeHours) && (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
              {data.clientHours && (
                <div className="lg:col-span-7">
                  <ClientHoursTable rows={data.clientHours} previousLabel={previousLabel} />
                </div>
              )}
              {data.employeeHours && (
                <div className="lg:col-span-5">
                  <TeamLoadList view="month" monthRows={data.employeeHours} previousLabel={previousLabel} />
                </div>
              )}
            </div>
          )}

          {showMonthBlocks && data.unbilledHours && <UnbilledHoursCard data={data.unbilledHours} />}

          {/* 4 · O meu trabalho — nas duas lentes */}
          {data.myWeek && <MyWeekCard data={data.myWeek} />}
        </>
      ) : null}
    </div>
  );
};

export default HomePage;
